import API_BASE from './config';
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';

const RegistrationForm = ({ segment, onSuccess }) => {
    const [leadName, setLeadName] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [phone, setPhone] = useState('');
    const [bkashNumber, setBkashNumber] = useState('');
    const [trxId, setTrxId] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('bKash');

    const todayStr = new Date().toISOString().split('T')[0];
    const [selectedSlotId, setSelectedSlotId] = useState('');
    const [searchDate, setSearchDate] = useState(todayStr);
    const [showDatePicker, setShowDatePicker] = useState(false);

    const [customData, setCustomData] = useState({});
    const [copied, setCopied] = useState(false);

    const [promoCode, setPromoCode] = useState('');
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [finalPrice, setFinalPrice] = useState(segment.is_free ? 0 : (parseFloat(segment.fee) || 0));
    const [isApplying, setIsApplying] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Team Logic States
    const isTeamEvent = segment.is_team_event || false;
    const isFreeSegment = segment.is_free === true;
    const minMembers = segment.min_team_size || 1;
    const maxMembers = segment.max_team_size || 1;
    const [teamName, setTeamName] = useState('');
    const [members, setMembers] = useState([]);

    // Concert Ticketing
    const isConcertTicketing = segment.is_concert_ticketing === true;

    const [selectedTierId, setSelectedTierId] = useState('');
    const [ticketQuantity, setTicketQuantity] = useState(1);
    const [ticketHolders, setTicketHolders] = useState([]);

    const paymentSystem = segment.payment_system || 'manual';
    const paymentNumbers = segment.payment_numbers || {};
    const activeSendNumber = paymentNumbers[paymentMethod] || "No Number Assigned";

    const isExpired = segment.reg_deadline && new Date() > new Date(segment.reg_deadline);
    const isStatic = segment.slot_behavior === 'static';
    const availableTiers = Array.isArray(segment.ticket_tiers)
        ? segment.ticket_tiers.filter(t => t.is_active)
        : [];
    const hasSlots = segment.slots && segment.slots.length > 0;

    // ── Pre-fill from participant account ─────────────────────────────────────
    useEffect(() => {
        const participantToken = localStorage.getItem('participant_access_token');
        if (!participantToken) return;

        try {
            const stored = JSON.parse(localStorage.getItem('participant_user') || 'null');
            if (stored) {
                const fullName = [stored.first_name, stored.last_name].filter(Boolean).join(' ');
                if (fullName) setLeadName(fullName);
                if (stored.email) setEmail(stored.email);
            }
        } catch { /* ignore */ }

        axios.get(`${API_BASE}/api/participant/profile/`, {
            headers: { Authorization: `Bearer ${participantToken}` }
        })
            .then(res => {
                const p = res.data;
                const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ');
                if (fullName) setLeadName(fullName);
                if (p.email) setEmail(p.email);
                if (p.phone) setPhone(p.phone);
            })
            .catch(() => { /* profile fetch optional — ignore errors */ });
    }, []);

    useEffect(() => {
        if (isTeamEvent) {
            // SAFE PARSING: Handle potential string from backend
            let blueprint = segment.member_fields_blueprint || {};
            if (typeof blueprint === 'string') {
                try {
                    blueprint = JSON.parse(blueprint);
                    if (typeof blueprint === 'string') blueprint = JSON.parse(blueprint);
                } catch (e) { blueprint = {}; }
            }

            const initialMembers = Array.from({ length: minMembers }, (_, i) => {
                const extraData = {};
                // Initialize keys from the object safely
                if (blueprint && typeof blueprint === 'object' && !Array.isArray(blueprint)) {
                    Object.keys(blueprint).forEach((label) => {
                        extraData[label] = '';
                    });
                }

                return {
                    name: i === 0 ? leadName : '',
                    email: i === 0 ? email : '',
                    phone: i === 0 ? phone : '',
                    is_lead: i === 0,
                    extra_data: extraData
                };
            });
            setMembers(initialMembers);
        }
    }, [isTeamEvent, minMembers, segment.member_fields_blueprint]);
    useEffect(() => {
        if (!isConcertTicketing) return;

        const holders = Array.from(
            { length: ticketQuantity },
            (_, i) => ({
                name: i === 0 ? leadName : '',
                email: i === 0 ? email : '',
                phone: i === 0 ? phone : '',
            })
        );

        setTicketHolders(holders);
    }, [ticketQuantity, isConcertTicketing]);
    // Keep lead member in sync with main contact fields
    useEffect(() => {
        if (isTeamEvent && members.length > 0) {
            setMembers(prev => prev.map((m, i) => {
                if (i === 0) {
                    return { ...m, name: leadName, email, phone };
                }
                return m;
            }));
        }
    }, [leadName, email, phone]);
    useEffect(() => {
        if (!isConcertTicketing) return;

        setTicketHolders(prev =>
            prev.map((holder, idx) => {
                if (idx === 0) {
                    return {
                        ...holder,
                        name: leadName,
                        email,
                        phone
                    };
                }
                return holder;
            })
        );
    }, [leadName, email, phone, isConcertTicketing]);
    const availableDateKeys = useMemo(() => {
        if (!hasSlots || isStatic) return [];
        return [...new Set(segment.slots.map(s => s.date_key).filter(Boolean))];
    }, [segment.slots, hasSlots, isStatic]);

    const filteredSlots = useMemo(() => {
        if (!hasSlots) return [];
        if (isStatic) return segment.slots;
        return segment.slots.filter(slot => slot.date_key === searchDate);
    }, [segment.slots, searchDate, hasSlots, isStatic]);

    const handleCopy = () => {
        navigator.clipboard.writeText(activeSendNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCustomInputChange = (label, value) => {
        setCustomData(prev => ({ ...prev, [label]: value }));
    };
    useEffect(() => {
        if (!isConcertTicketing) return;

        const tier = availableTiers.find(
            t => String(t.id) === String(selectedTierId)
        );

        if (!tier) {
            setFinalPrice(0);
            return;
        }

        const price =
            parseFloat(tier.price || 0) *
            parseInt(ticketQuantity || 1);

        setFinalPrice(price);
    }, [
        selectedTierId,
        ticketQuantity,
        isConcertTicketing
    ]);
    const handleApplyPromo = async () => {
        if (!promoCode.trim()) return;
        setIsApplying(true);
        try {
            const res = await axios.post(`${API_BASE}/api/registrations/validate-promo/`, {
                code: promoCode.trim(),
                segment_id: segment.id
            });
            const promo = res.data;
            const discount = promo.discount_type === 'percentage'
                ? (parseFloat(segment.fee) * parseFloat(promo.discount_value)) / 100
                : parseFloat(promo.discount_value);
            setFinalPrice(Math.max(0, parseFloat(segment.fee) - discount));
            setAppliedPromo(promo);
        } catch (err) {
            alert(err.response?.data?.error || "Invalid or inactive promo code.");
            setAppliedPromo(null);
            setFinalPrice(parseFloat(segment.fee) || 0);
        } finally {
            setIsApplying(false);
        }
    };

    const handleRemovePromo = () => {
        setAppliedPromo(null);
        setPromoCode('');
        setFinalPrice(parseFloat(segment.fee) || 0);
    };

    const addMember = () => {
        if (members.length < maxMembers) {
            const emptyExtra = {};
            if (segment.member_fields_blueprint) {
                Object.keys(segment.member_fields_blueprint).forEach(label => emptyExtra[label] = '');
            }
            setMembers([...members, { name: '', email: '', phone: '', is_lead: false, extra_data: emptyExtra }]);
        }
    };

    const removeMember = (index) => {
        if (members.length > minMembers) {
            setMembers(members.filter((_, i) => i !== index));
        }
    };

    const handleMemberChange = (index, field, value) => {
        const updated = [...members];
        updated[index][field] = value;
        setMembers(updated);
    };

    const handleMemberExtraChange = (mIndex, label, value) => {
        const updated = [...members];
        updated[mIndex].extra_data = { ...updated[mIndex].extra_data, [label]: value };
        setMembers(updated);
    };
    const handleTicketHolderChange = (index, field, value) => {
        const updated = [...ticketHolders];
        updated[index][field] = value;
        setTicketHolders(updated);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('participant_access_token');
            
            if (!token) {
                alert("Please log in first.");
                setLoading(false);
                return;
            }
            if (isConcertTicketing) {
                if (!selectedTierId) {
                    alert("Please select a ticket tier.");
                    setLoading(false);
                    return;
                }

                if (!ticketQuantity || ticketQuantity < 1) {
                    alert("Please select ticket quantity.");
                    setLoading(false);
                    return;
                }

                const holderEmails = ticketHolders.map(holder =>
                    (holder.email || '').trim().toLowerCase()
                );

                const uniqueHolderEmails = new Set(holderEmails);

                if (uniqueHolderEmails.size !== holderEmails.length) {
                    alert("Each concert ticket holder must use a unique email address.");
                    setLoading(false);
                    return;
                }
            }

            // 1. Prepare the payload with the "Dummy Team" logic for individual events
            const formData = new FormData();

            formData.append('segment', Number(segment.id));
            formData.append('lead_name', leadName.trim());
            formData.append('email', email.trim().toLowerCase());
            formData.append('phone', phone.trim());
            formData.append('bkash_number', isFreeSegment ? 'FREE' : (paymentSystem === 'gateway' ? 'GATEWAY' : bkashNumber.trim()));
            formData.append('trx_id', isFreeSegment ? `FREE-${Date.now()}` : (paymentSystem === 'gateway' ? `GTW-${Date.now()}` : trxId.trim().toUpperCase()));
            formData.append('payment_method', isFreeSegment ? 'Free' : (paymentSystem === 'gateway' ? 'Gateway' : paymentMethod));
            formData.append('paid_amount', isFreeSegment ? '0' : String(finalPrice));
            formData.append('applied_promo', (appliedPromo && appliedPromo.id) ? Number(appliedPromo.id) : '');
            formData.append('virtual_slot_id', selectedSlotId || '');
            if (isConcertTicketing) {
                formData.append('ticket_tier_id', selectedTierId);
                formData.append('ticket_quantity', String(ticketQuantity));

                formData.append('ticket_holders', JSON.stringify(ticketHolders.map(holder => ({
                    name: (holder.name || '').trim(),
                    email: (holder.email || '').trim().toLowerCase(),
                    phone: (holder.phone || '').trim(),
                }))));
            }

            if (isTeamEvent) {
                formData.append('team_name', teamName.trim());
            }

            if (isTeamEvent) {
                formData.append('members', JSON.stringify(members.map(m => ({
                    name: (m.name || '').trim(),
                    email: (m.email || '').trim().toLowerCase(),
                    phone: (m.phone || '').trim(),
                    is_lead: !!m.is_lead,
                    extra_data: typeof m.extra_data === 'object' ? m.extra_data : {}
                }))));
            }

            const submittedData = {};

            Object.entries(customData).forEach(([key, value]) => {
                if (value instanceof File) {
                    formData.append(`file_${key}`, value);
                } else {
                    submittedData[key] = value;
                }
            });

            formData.append('submitted_data', JSON.stringify(submittedData));

            // 2. Perform the actual POST request
            const res = await axios({
                method: 'post',
                url: `${API_BASE}/api/registrations/`,
                data: formData,
                headers: {
                    'Authorization': `Bearer ${token.trim()}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            // 3. Success handling
            if (res && (res.status === 201 || res.status === 200)) {
                window.location.href = '/registration-success';
            }

        } catch (err) {
            console.error("SUBMISSION ERROR:", err.response?.data);
            const errorData = err.response?.data;

            // Handle 500 error HTML responses gracefully
            const msg = errorData
                ? (typeof errorData === 'string' && errorData.includes('<!DOCTYPE')
                    ? "Server Error (500). Please ensure your TrxID is unique and all fields are valid."
                    : JSON.stringify(errorData))
                : err.message;

            if (
                err.response?.status === 401 ||
                JSON.stringify(err.response?.data || '').includes('token_not_valid') ||
                JSON.stringify(err.response?.data || '').includes('Token is expired')
            ) {
                localStorage.removeItem('participant_access_token');
                localStorage.removeItem('participant_refresh_token');
                localStorage.removeItem('participant_user');

                alert("Your session expired. Please login again.");
                window.location.href = "/participant-login";
                return;
            }

            alert("Submission Failed: " + msg);
        } finally {
            setLoading(false);
        }
    };
    const PaymentInfoSection = ({ showHeader = false }) => (
        <div className="pt-5 border-t border-white/[0.05] space-y-5">
            {showHeader && <h3 className="text-[10px] font-black text-sky-500 uppercase tracking-[0.3em] mb-2 ml-1">Payment Information</h3>}
            {paymentSystem === 'manual' ? (
                <div className="space-y-4">
                    <div className="bg-sky-500/5 border border-sky-500/20 p-4 rounded-2xl flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.2em] mb-1">{paymentMethod} — Send Money</p>
                            <p className="text-white text-base font-black tracking-tight truncate">{activeSendNumber}</p>
                        </div>
                        <button type="button" onClick={handleCopy}
                            className={`shrink-0 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 ${copied ? 'bg-emerald-500 text-white' : 'bg-white text-black'}`}>
                            {copied ? '✓ Copied' : 'Copy'}
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">Method</label>
                            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                                className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/40 font-bold text-sm">
                                <option value="bKash" className="bg-[#050505]">bKash</option>
                                <option value="Nagad" className="bg-[#050505]">Nagad</option>
                                <option value="Rocket" className="bg-[#050505]">Rocket</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">Sender No.</label>
                            <input type="tel" required placeholder="01XXX" value={bkashNumber} onChange={e => setBkashNumber(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/40 text-sm" />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">TrxID *</label>
                            <input type="text" required placeholder="Paste TrxID" value={trxId} onChange={e => setTrxId(e.target.value)}
                                className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/40 font-mono text-sm" />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-sky-500/5 p-6 rounded-2xl border border-sky-500/20 text-center">
                    <h4 className="text-white font-black mb-2 uppercase tracking-tight">Merchant Checkout Active</h4>
                    <p className="text-gray-500 text-xs">You will be redirected to the secure payment portal after clicking proceed.</p>
                </div>
            )}
        </div>
    );

    if (isExpired) {
        return (
            <div className="relative overflow-hidden bg-white/[0.02] backdrop-blur-3xl p-8 md:p-20 rounded-3xl border border-red-500/20 text-center">
                <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.5em] mb-4">System Offline</p>
                <h2 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tighter italic leading-none mb-4">Registration Closed</h2>
                <p className="text-gray-500 font-bold uppercase text-[9px] tracking-widest">The deadline for this segment has passed.</p>
            </div>
        );
    }

    return (
        <div className="relative overflow-hidden bg-[#080b10]/95 backdrop-blur-3xl rounded-[2rem] md:rounded-[2.5rem] border border-white/10 shadow-[0_40px_100px_-30px_rgba(14,165,233,0.35)] animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-40 h-40 bg-sky-500/5 rounded-full blur-[80px] pointer-events-none" />

            {/* BANNER */}
            {segment.banner && (
                <div className="overflow-hidden border-b border-white/5">
                    <img src={segment.banner} alt={segment.title} className="w-full h-36 sm:h-52 md:h-64 object-cover" />
                </div>
            )}

            {localStorage.getItem('participant_access_token') && (
                <div className="mx-5 sm:mx-8 md:mx-12 mt-5 flex items-center gap-2.5 bg-sky-500/5 border border-sky-500/15 rounded-2xl px-4 py-3">
                    <div className="w-5 h-5 bg-sky-500/20 rounded-full flex items-center justify-center shrink-0">
                        <svg className="w-3 h-3 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <p className="text-[10px] font-bold text-sky-400/80">
                        Signed in — this ticket will appear in your <strong className="text-sky-400">My Tickets</strong> after verification.
                    </p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="p-5 sm:p-8 md:p-10 space-y-7 relative z-10">
                {/* HEADER */}
                <div>
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <p className="text-[9px] font-bold text-sky-500 uppercase tracking-[0.4em] mb-1.5">System Infrastructure</p>
                            <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight uppercase leading-none">{segment.title}</h3>
                            {segment.access_note && (
                                <p className="text-[10px] font-black text-sky-500 uppercase tracking-[0.2em] mt-1.5 italic">{segment.access_note}</p>
                            )}
                        </div>
                        <div className="bg-white/[0.03] px-4 py-3 rounded-xl border border-white/[0.05] shrink-0 text-left sm:text-right">
                            <span className="block text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1">Fee</span>
                            <span className={`text-xl font-extrabold tracking-tight ${appliedPromo ? 'text-emerald-500' : 'text-white'}`}>
                                {finalPrice} <span className="text-xs font-medium text-gray-400">BDT</span>
                            </span>
                            {appliedPromo && (
                                <span className="block text-[8px] font-black text-gray-600 line-through uppercase tracking-widest mt-0.5">Was {segment.fee} BDT</span>
                            )}
                        </div>
                    </div>
                    {segment.description && (
                        <div className="mt-5 p-4 bg-white/[0.02] border border-white/[0.05] rounded-2xl">
                            <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.2em] mb-1.5">Guidelines</p>
                            <p className="text-gray-400 text-xs leading-relaxed italic">{segment.description}</p>
                        </div>
                    )}
                    {segment.rulebook_link && (
                        <div className="mt-5 p-4 bg-white/[0.02] border border-white/[0.05] rounded-2xl">
                            <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.2em] mb-1.5">Rulebook</p>
                            <a href={segment.rulebook_link} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-400 underline">
                                View Rulebook
                            </a>
                        </div>
                    )}
                </div>

                {/* CORE FIELDS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">Full Name *</label>
                        <input type="text" required placeholder="Enter Full Name" value={leadName} onChange={e => setLeadName(e.target.value)}
                            className="w-full bg-black/50 border border-white/10 rounded-2xl px-4 py-4 text-white outline-none focus:border-sky-400/70 focus:ring-2 focus:ring-sky-500/10 text-sm transition-all placeholder:text-gray-700" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">Email *</label>
                        <input type="email" required placeholder="Enter Email" value={email} onChange={e => setEmail(e.target.value)}
                            className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/50 text-sm" />
                    </div>
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">Phone *</label>
                    <input type="tel" required placeholder="01XXXXXXXX" value={phone} onChange={e => setPhone(e.target.value)}
                        className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/50 text-sm" />
                </div>
                {isConcertTicketing && (
                    <div className="space-y-6 animate-in fade-in duration-500">
                        <div className="bg-sky-500/5 p-5 rounded-2xl border border-sky-500/20">
                            <p className="text-[10px] font-black text-sky-500 uppercase tracking-widest mb-2">
                                Concert Ticketing
                            </p>

                            <p className="text-xs text-gray-400 leading-relaxed">
                                You can buy multiple tickets in one registration. Each ticket will be sent to the email address entered below.
                                Please enter every ticket holder email correctly.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">
                                    Ticket Tier *
                                </label>

                                <select
                                    required={isConcertTicketing}
                                    value={selectedTierId}
                                    onChange={e => setSelectedTierId(e.target.value)}
                                    className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/50 text-sm"
                                >
                                    <option value="" className="bg-[#050505]">
                                        Select ticket tier
                                    </option>

                                    {availableTiers.map(tier => {
                                        const remaining = Number(tier.remaining_capacity ?? tier.capacity ?? 0);
                                        const isSoldOut = remaining <= 0;

                                        return (
                                            <option
                                                key={tier.id}
                                                value={tier.id}
                                                disabled={isSoldOut}
                                                className="bg-[#050505]"
                                            >
                                                {tier.name} — {tier.price} BDT {isSoldOut ? '(SOLD OUT)' : `(${remaining} left)`}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">
                                    Quantity *
                                </label>

                                <select
                                    required={isConcertTicketing}
                                    value={ticketQuantity}
                                    onChange={e => setTicketQuantity(parseInt(e.target.value))}
                                    className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/50 text-sm"
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(q => (
                                        <option key={q} value={q} className="bg-[#050505]">
                                            {q} Ticket{q > 1 ? 's' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4">
                            <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-1">
                                Important Email Warning
                            </p>
                            <p className="text-xs text-amber-200/80 leading-relaxed">
                                Each QR ticket will be sent to the individual email written for that holder.
                                Wrong email means that person may not receive their ticket.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                                Ticket Holders ({ticketHolders.length})
                            </p>

                            {ticketHolders.map((holder, index) => (
                                <div
                                    key={index}
                                    className="bg-white/[0.03] border border-white/10 rounded-[2rem] p-6 space-y-4"
                                >
                                    <div className="flex justify-between items-center">
                                        <span className="text-[10px] font-black text-sky-500 uppercase tracking-widest">
                                            Ticket Holder {String(index + 1).padStart(2, '0')}
                                            {index === 0 ? ' (Buyer)' : ''}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <input
                                            type="text"
                                            required={isConcertTicketing}
                                            placeholder="Full Name"
                                            value={holder.name}
                                            readOnly={index === 0}
                                            onChange={e => handleTicketHolderChange(index, 'name', e.target.value)}
                                            className="bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/20"
                                        />

                                        <input
                                            type="email"
                                            required={isConcertTicketing}
                                            placeholder="Email Address"
                                            value={holder.email}
                                            readOnly={index === 0}
                                            onChange={e => handleTicketHolderChange(index, 'email', e.target.value)}
                                            className="bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/20"
                                        />
                                    </div>

                                    <input
                                        type="tel"
                                        placeholder="Phone Number (optional)"
                                        value={holder.phone}
                                        readOnly={index === 0}
                                        onChange={e => handleTicketHolderChange(index, 'phone', e.target.value)}
                                        className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/20"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {isTeamEvent && (
                    <div className="space-y-6 animate-in fade-in duration-500">
                        {/* TEAM NAME */}
                        <div className="bg-sky-500/5 p-5 rounded-2xl border border-sky-500/20">
                            <label className="block text-[10px] font-black text-sky-500 uppercase tracking-widest mb-2">Team Identity *</label>
                            <input type="text" required placeholder="Enter Team Name" value={teamName} onChange={e => setTeamName(e.target.value)}
                                className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-sky-500" />
                        </div>

                        {/* MEMBERS LIST */}
                        <div className="space-y-4">
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">Team Members ({members.length}/{maxMembers})</p>
                            {members.map((member, mIdx) => (
                                <div key={mIdx} className="bg-white/[0.03] border border-white/10 rounded-[2rem] p-6 space-y-4 relative">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-[10px] font-black text-sky-500 uppercase tracking-widest">
                                            {mIdx === 0 ? 'Member 01 (Lead)' : `Member 0${mIdx + 1}`}
                                        </span>
                                        {mIdx >= minMembers && (
                                            <button type="button" onClick={() => removeMember(mIdx)} className="text-red-500 text-[10px] font-black uppercase tracking-tighter">Remove</button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <input type="text" required placeholder="Full Name" value={member.name}
                                            readOnly={mIdx === 0}
                                            onChange={e => handleMemberChange(mIdx, 'name', e.target.value)}
                                            className="bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/20" />
                                        <input type="email" required placeholder="Email Address" value={member.email}
                                            readOnly={mIdx === 0}
                                            onChange={e => handleMemberChange(mIdx, 'email', e.target.value)}
                                            className="bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-white/20" />
                                    </div>

                                    {/* REPLICATED MEMBER FIELDS - RENDERING FIX */}
                                    {(() => {
                                        let blueprint = segment.member_fields_blueprint || {};

                                        // THE UNWRAPPER: If it's a string, turn it into an object
                                        if (typeof blueprint === 'string') {
                                            try {
                                                blueprint = JSON.parse(blueprint);
                                                // If it's still a string (double-encoded), parse again
                                                if (typeof blueprint === 'string') blueprint = JSON.parse(blueprint);
                                            } catch (e) {
                                                blueprint = {};
                                            }
                                        }

                                        // Now safely map over the keys
                                        if (blueprint && typeof blueprint === 'object' && !Array.isArray(blueprint)) {
                                            return Object.keys(blueprint).map((fieldLabel, fIdx) => (
                                                <div key={fIdx} className="mt-3">
                                                    <label className="block text-[10px] font-black text-sky-500 uppercase tracking-widest mb-1 ml-1">
                                                        {fieldLabel}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder={`Enter ${fieldLabel}`}
                                                        value={member.extra_data?.[fieldLabel] || ''}
                                                        onChange={e => handleMemberExtraChange(mIdx, fieldLabel, e.target.value)}
                                                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-sky-500 transition-all"
                                                    />
                                                </div>
                                            ));
                                        }
                                        return null;
                                    })()}
                                </div>
                            ))}

                            {members.length < maxMembers && (
                                <button type="button" onClick={addMember}
                                    className="w-full py-4 border-2 border-dashed border-white/10 rounded-[2rem] text-gray-600 text-[10px] font-black uppercase tracking-widest hover:border-sky-500/40 hover:text-sky-500 transition-all active:scale-95">
                                    + Add Team Member
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* CUSTOM FIELDS (Segment Specific) */}
                {segment.custom_form_fields?.filter(field => field && field.label).map((field, index) => (
                    <div key={index}>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">
                            {field.label} {field.required && <span className="text-sky-500">*</span>}
                        </label>
                        {field.type === 'select' ? (
                            <div className="grid grid-cols-2 gap-2">
                                {field.options?.map((option, optIdx) => (
                                    <button key={optIdx} type="button" onClick={() => handleCustomInputChange(field.label, option)}
                                        className={`p-3 rounded-xl border text-[10px] font-bold uppercase tracking-wider transition-all ${customData[field.label] === option ? 'bg-sky-500 border-sky-500 text-black' : 'bg-black/40 border-white/[0.07] text-gray-400 hover:border-white/20'}`}>
                                        {option}
                                    </button>
                                ))}
                            </div>
                        ) : (
                                <input
                                    type={field.type || "text"}
                                    required={field.required}
                                    placeholder={`Enter ${(field.label || '').toLowerCase()}`}
                                    onChange={e => {
                                        if ((field.type || '').toLowerCase() === 'file') {
                                            handleCustomInputChange(field.label, e.target.files[0]);
                                        } else {
                                            handleCustomInputChange(field.label, e.target.value);
                                        }
                                    }}
                                    className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/50 text-sm"
                                />
                        )}
                    </div>
                ))}

                {/* PROMO CODE BLOCK */}
                {!isConcertTicketing && (
                <div className="bg-white/[0.02] p-4 sm:p-5 rounded-2xl border border-white/[0.05]">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">Promo Code</label>
                    {!appliedPromo ? (
                        <div className="flex gap-2">
                            <input type="text" placeholder="ENTER CODE" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())}
                                className="flex-1 min-w-0 bg-black/60 border border-white/[0.07] rounded-xl px-4 py-3 text-white outline-none focus:border-sky-500/40 font-mono text-xs uppercase" />
                            <button type="button" disabled={isApplying || !promoCode.trim()} onClick={handleApplyPromo}
                                className="shrink-0 bg-white text-black px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest disabled:opacity-30 transition-all active:scale-95">
                                {isApplying ? '...' : 'Apply'}
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3 gap-3">
                            <div className="min-w-0">
                                <p className="text-[10px] text-emerald-400 font-black uppercase tracking-widest truncate">{promoCode} — {parseFloat(segment.fee) - finalPrice} BDT saved</p>
                                <p className="text-[9px] text-emerald-600 mt-0.5">Applied successfully</p>
                            </div>
                            <button type="button" onClick={handleRemovePromo} className="shrink-0 text-[9px] font-black text-red-500 uppercase tracking-widest active:scale-95">Remove</button>
                        </div>
                    )}
                </div>
                )}

                {/* PAYMENT SECTION (Always in the bottom as requested) */}
                {/* PAYMENT SECTION - INLINED TO PREVENT FOCUS LOSS */}
                {!isFreeSegment && (
                    <div className="pt-5 border-t border-white/[0.05] space-y-5">
                    {isTeamEvent && <h3 className="text-[10px] font-black text-sky-500 uppercase tracking-[0.3em] mb-2 ml-1">Payment Information</h3>}
                    {paymentSystem === 'manual' ? (
                        <div className="space-y-4">
                            <div className="bg-sky-500/5 border border-sky-500/20 p-4 rounded-2xl flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.2em] mb-1">{paymentMethod} — Send Money</p>
                                    <p className="text-white text-base font-black tracking-tight truncate">{activeSendNumber}</p>
                                </div>
                                <button type="button" onClick={handleCopy}
                                    className={`shrink-0 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 ${copied ? 'bg-emerald-500 text-white' : 'bg-white text-black'}`}>
                                    {copied ? '✓ Copied' : 'Copy'}
                                </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">Method</label>
                                    <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                                        className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/40 font-bold text-sm">
                                        <option value="bKash" className="bg-[#050505]">bKash</option>
                                        <option value="Nagad" className="bg-[#050505]">Nagad</option>
                                        <option value="Rocket" className="bg-[#050505]">Rocket</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">Sender No.</label>
                                    <input type="tel" required placeholder="01XXX" value={bkashNumber} onChange={e => setBkashNumber(e.target.value)}
                                        className="w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/40 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2 ml-1">TrxID *</label>
                                    <input type="text" required placeholder="Paste TrxID" value={trxId} onChange={e => setTrxId(e.target.value)}
                                        className="w-full bg-black/40 border border-white/[0.07] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-sky-500/40 font-mono text-sm" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-sky-500/5 p-6 rounded-2xl border border-sky-500/20 text-center">
                            <h4 className="text-white font-black mb-2 uppercase tracking-tight">Merchant Checkout Active</h4>
                            <p className="text-gray-500 text-xs">You will be redirected to the secure payment portal after clicking proceed.</p>
                        </div>
                    )}
                </div>
                )}

                {/* SLOTS (Logic preserved) */}
                {hasSlots && (
                    <div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                Available Slots <span className="text-sky-500">*</span>
                            </label>
                            {!isStatic && (
                                <div className="flex items-center gap-2">
                                    <button type="button"
                                        onClick={() => { setSearchDate(todayStr); setShowDatePicker(false); setSelectedSlotId(''); }}
                                        className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all ${searchDate === todayStr && !showDatePicker ? 'bg-sky-500 border-sky-500 text-black' : 'bg-black/40 border-white/10 text-gray-400 hover:border-white/20'}`}>
                                        Today
                                    </button>
                                    <button type="button"
                                        onClick={() => setShowDatePicker(prev => !prev)}
                                        className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all ${(showDatePicker || searchDate !== todayStr) ? 'bg-sky-500 border-sky-500 text-black' : 'bg-black/40 border-white/10 text-gray-400 hover:border-white/20'}`}>
                                        {searchDate !== todayStr
                                            ? new Date(searchDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                            : 'Other Date'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {!isStatic && showDatePicker && (
                            <div className="mb-4 animate-in slide-in-from-top-2 duration-200">
                                <input type="date" value={searchDate} min={todayStr}
                                    onChange={e => { setSearchDate(e.target.value); setSelectedSlotId(''); setShowDatePicker(false); }}
                                    className="w-full sm:w-auto bg-white/5 border border-sky-500/40 rounded-xl px-4 py-3 text-sky-400 text-sm font-bold outline-none focus:border-sky-500 transition-all" />
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-2">
                            {filteredSlots.length > 0 ? filteredSlots.map(slot => (
                                <button key={slot.id} type="button" disabled={slot.is_full}
                                    onClick={() => setSelectedSlotId(slot.id)}
                                    className={`p-4 rounded-2xl border text-left transition-all ${selectedSlotId === slot.id
                                        ? 'bg-sky-500 border-sky-500 shadow-[0_0_16px_rgba(14,165,233,0.2)]'
                                        : slot.is_full
                                            ? 'bg-white/5 border-white/5 opacity-40 cursor-not-allowed'
                                            : 'bg-black/40 border-white/10 hover:border-sky-500/50 active:scale-[0.99]'
                                        }`}>
                                    <div className="flex justify-between items-center">
                                        <div className="min-w-0">
                                            <p className={`text-xs font-black uppercase truncate ${selectedSlotId === slot.id ? 'text-black' : 'text-white'}`}>{slot.label}</p>
                                            <p className={`text-[8px] font-bold uppercase mt-0.5 ${selectedSlotId === slot.id ? 'text-black/60' : 'text-gray-500'}`}>
                                                {isStatic ? 'Fixed Seat' : 'Managed Inventory'}
                                            </p>
                                        </div>
                                        {slot.is_full ? (
                                            <span className="shrink-0 text-[9px] font-black uppercase bg-red-500/20 text-red-500 px-2.5 py-1 rounded-lg ml-2">Full</span>
                                        ) : (
                                            <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ml-2 ${selectedSlotId === slot.id ? 'border-black' : 'border-white/20'}`}>
                                                {selectedSlotId === slot.id && <div className="w-2 h-2 bg-black rounded-full" />}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            )) : (
                                <div className="p-8 text-center border border-dashed border-white/5 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-600 uppercase tracking-[0.3em]">
                                        {isStatic ? 'No slots configured.' : `No slots for ${new Date(searchDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}.`}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* FINAL SUBMIT BUTTON */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-white hover:bg-sky-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black py-4.5 rounded-2xl uppercase tracking-[0.22em] transition-all shadow-[0_20px_40px_-20px_rgba(255,255,255,0.7)] active:scale-[0.99]"
                >
                    {loading ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5 text-black" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                        </span>
                    ) : (
                            isFreeSegment
                                ? 'Complete Free Registration'
                                : isConcertTicketing
                                    ? `Buy ${ticketQuantity} Ticket${ticketQuantity > 1 ? 's' : ''} (${finalPrice} BDT)`
                                    : `Complete Registration (${finalPrice} BDT)`
                    )}
                </button>
            </form>
        </div>
    );
};

export default RegistrationForm;